import { useSyncExternalStore } from 'react';

/** A help page, and optionally the section to scroll to. */
export interface HelpPageTarget {
  pageId: string;
  anchor?: string;
}

/** A field's help; the id is `{Entity}.{field}`. */
export interface HelpFieldTarget {
  fieldId: string;
}

/** The fields of an entity's form, with their help texts: "Show field information" on the screen's help. */
export interface HelpFieldsTarget {
  fieldsOf: string;
  /** The entity's module, as the page context names it (`Finance`): where its form and its field restrictions live. */
  module: string;
}

export type HelpTarget = HelpPageTarget | HelpFieldTarget | HelpFieldsTarget;

export const isFieldTarget = (target: HelpTarget): target is HelpFieldTarget => 'fieldId' in target;

export const isFieldsTarget = (target: HelpTarget): target is HelpFieldsTarget => 'fieldsOf' in target;

export interface HelpPanelState {
  open: boolean;
  /**
   * The page, field or form's fields shown; null shows the search results, the index or the help for the
   * current screen.
   */
  target: HelpTarget | null;
  /** The index of all help, per module, instead of the help for the current screen. */
  browsing: boolean;
  /** The search typed in the panel. It stays while a hit is open, so going back shows the results again. */
  query: string;
  /** The module the search results are narrowed to, kept with the search; null for every module. */
  module: string | null;
  /** The modules opened in the index; the others show only their name and number of pages. */
  indexOpen: string[];
}

const HELP_SCHEME = 'help:';
const FIELD_SCHEME = 'help-field:';

/**
 * Reads a help link: `help:finance.plan-version-lock`, `help:finance.plan-version-lock#lock` — the form
 * help pages link with — or `help-field:PlanVersion.versionNo`, the form the quick search's field hits
 * carry. Null for anything else.
 */
export function parseHelpLink(href: string | undefined): HelpTarget | null {
  if (href?.startsWith(FIELD_SCHEME)) {
    const fieldId = href.slice(FIELD_SCHEME.length);
    return fieldId ? { fieldId } : null;
  }
  if (!href?.startsWith(HELP_SCHEME)) return null;
  const [pageId, anchor] = href.slice(HELP_SCHEME.length).split('#', 2);
  if (!pageId) return null;
  return anchor ? { pageId, anchor } : { pageId };
}

/** Where the panel's last view is kept, so it opens on it again — also after a reload of the tab. */
const STORAGE_KEY = 'nexo.help.panel';

const CLOSED: HelpPanelState = { open: false, target: null, browsing: false, query: '', module: null, indexOpen: [] };

let state: HelpPanelState = { ...CLOSED, ...readStored() };
const listeners = new Set<() => void>();

function setState(next: HelpPanelState) {
  state = next;
  store();
  listeners.forEach((listener) => listener());
}

function store() {
  try {
    const { target, browsing, query, module, indexOpen } = state;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ target, browsing, query, module, indexOpen }));
  } catch {
    // Storage blocked or full: the panel still keeps its view for as long as the page lives.
  }
}

/** The view kept from before, each part checked: anything malformed is left out. */
function readStored(): Partial<HelpPanelState> {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null') as Record<string, unknown> | null;
    if (!stored || typeof stored !== 'object') return {};
    return {
      target: storedTarget(stored.target),
      browsing: stored.browsing === true,
      query: typeof stored.query === 'string' ? stored.query : '',
      module: typeof stored.module === 'string' ? stored.module : null,
      indexOpen: Array.isArray(stored.indexOpen) ? stored.indexOpen.filter((m): m is string => typeof m === 'string') : [],
    };
  } catch {
    return {};
  }
}

function storedTarget(value: unknown): HelpTarget | null {
  if (!value || typeof value !== 'object') return null;
  const target = value as Record<string, unknown>;
  if (typeof target.fieldId === 'string') return { fieldId: target.fieldId };
  if (typeof target.fieldsOf === 'string' && typeof target.module === 'string') {
    return { fieldsOf: target.fieldsOf, module: target.module };
  }
  if (typeof target.pageId !== 'string') return null;
  return typeof target.anchor === 'string' ? { pageId: target.pageId, anchor: target.anchor } : { pageId: target.pageId };
}

/**
 * The help panel's state, shared app-wide: the header button, the Cmd-K dialog and `help:` links in
 * help text all open the one panel the Layout renders.
 */
export const helpPanel = {
  /**
   * The header button: what the panel showed last — the search with its module, the page or field that
   * was open, or the index — or, the first time, the help for the screen.
   */
  openForScreen: () => setState({ ...state, open: true }),
  /** From outside the panel (the Cmd-K dialog): this page or field, with no earlier search or index to go back to. */
  openAt: (target: HelpTarget) => setState({ ...state, open: true, target, browsing: false, query: '', module: null }),
  /** From outside the panel (Cmd-Enter on a help hit in the Cmd-K dialog): this search, over every module. */
  openSearch: (query: string) => setState({ ...state, open: true, target: null, browsing: false, query, module: null }),
  /** Within the panel: keeps the search and the index, so going back returns to them. */
  openTarget: (target: HelpTarget) => setState({ ...state, open: true, target }),
  browse: () => setState({ ...state, open: true, target: null, browsing: true }),
  /** Typing a search shows its results, leaving the page or field that was open. */
  search: (query: string) => setState({ ...state, query, target: null }),
  /** Narrows the search results to one module, or to every module with null. */
  filterModule: (module: string | null) => setState({ ...state, module }),
  /** Opens or closes a module in the index; the choice is kept, so going back to the index finds it as it was. */
  toggleIndexModule: (module: string) =>
    setState({
      ...state,
      indexOpen: state.indexOpen.includes(module) ? state.indexOpen.filter((m) => m !== module) : [...state.indexOpen, module],
    }),
  /**
   * One step back: from a page, a field or a form's fields to where it was opened from, from the index to
   * the screen's help.
   */
  back: () => setState(state.target ? { ...state, target: null } : { ...state, browsing: false }),
  close: () => setState({ ...state, open: false }),
};

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useHelpPanel(): HelpPanelState {
  return useSyncExternalStore(subscribe, () => state);
}
